import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import WeeklyChart from './components/WeeklyChart';
import AchievementBadges from './components/AchievementBadges';
import GoalSettingPanel from './components/GoalSettingPanel';
import QuickActionWidget from './components/QuickActionWidget';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { duelService } from '../../services/socialService';
import { workoutService } from '../../services/trackingService';
import { useDashboardMetrics } from '../../hooks/useRealtimeMetrics';
import milestoneService from '../../services/milestoneService';
import MilestonePopup from '../../components/MilestonePopup';
import { achievementCardService } from '../../services/achievementCardService';

const MetricCard = ({ title, value, target, unit, icon, color, percentage, trend, onClick }) => {
  const getGreeting = () => {
    if (percentage >= 100) return { label: 'Goal reached!', color: 'text-emerald-600 dark:text-emerald-400' };
    if (percentage >= 75) return { label: 'Almost there', color: 'text-green-600 dark:text-green-400' };
    if (percentage >= 50) return { label: 'Halfway there', color: 'text-yellow-600 dark:text-yellow-400' };
    if (percentage >= 25) return { label: 'Keep going', color: 'text-orange-500 dark:text-orange-400' };
    return { label: 'Just started', color: 'text-muted-foreground' };
  };

  const status = getGreeting();
  const formatValue = (val) => {
    if (typeof val !== 'number') return val;
    return val % 1 !== 0 ? val?.toFixed(1) : val?.toLocaleString();
  };

  return (
    <motion.button
      className="w-full text-left bg-card border border-border rounded-2xl p-5 hover:shadow-wellness-lg transition-all duration-200 group"
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}18` }}
          >
            <Icon name={icon} size={20} color={color} />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className={`text-xs font-medium mt-0.5 ${status?.color}`}>{status?.label}</p>
          </div>
        </div>
        {trend !== 0 && (
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
            style={{
              backgroundColor: trend > 0 ? '#48BB7815' : '#F5656515',
              color: trend > 0 ? '#48BB78' : '#F56565'
            }}
          >
            <Icon name={trend > 0 ? 'TrendingUp' : 'TrendingDown'} size={11} />
            <span>{trend > 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </div>
      {/* Value */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-bold text-foreground leading-none">{formatValue(value)}</span>
          <span className="text-sm text-muted-foreground font-medium">{unit}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">of {formatValue(target)} {unit} goal</p>
      </div>
      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">Progress</span>
          <span className="text-xs font-bold" style={{ color }}>{percentage}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percentage, 100)}%` }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
          />
        </div>
      </div>
    </motion.button>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { isDark } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState('');
  const [greetingEmoji, setGreetingEmoji] = useState('👋');
  const [isLoading, setIsLoading] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [activeDuels, setActiveDuels] = useState([]);
  const [metricSyncStatus, setMetricSyncStatus] = useState({
    steps: 'synced',
    calories: 'synced',
    water: 'synced',
    sleep: 'synced'
  });

  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);
  const [achievementCard, setAchievementCard] = useState(null);
  const [showAchievementCard, setShowAchievementCard] = useState(false);
  const [weeklyWorkoutLogs, setWeeklyWorkoutLogs] = useState([]);
  const [weeklyLogsLoading, setWeeklyLogsLoading] = useState(false);

  const { metrics: realtimeMetrics, loading: metricsLoading } = useDashboardMetrics(user?.id);

  useEffect(() => {
    if (location?.state?.message) {
      setWelcomeMessage(location?.state?.message);
      setTimeout(() => setWelcomeMessage(null), 5000);
    }
  }, [location]);

  useEffect(() => {
    const fetchActiveDuels = async () => {
      if (!user?.id) return;
      try {
        const duels = await duelService?.getActiveDuels(user?.id);
        setActiveDuels(duels);
      } catch (error) {
        console.error('Error fetching active duels:', error);
      }
    };
    fetchActiveDuels();
  }, [user?.id]);

  useEffect(() => {
    const fetchWeeklyLogs = async () => {
      if (!user?.id) return;
      setWeeklyLogsLoading(true);
      try {
        const today = new Date();
        const weekAgo = new Date(today);
        weekAgo?.setDate(today?.getDate() - 6);
        const startDate = weekAgo?.toISOString()?.split('T')?.[0];
        const endDate = today?.toISOString()?.split('T')?.[0];
        const logs = await workoutService?.getWorkouts(user?.id, startDate, endDate);
        setWeeklyWorkoutLogs(logs || []);
      } catch (error) {
        console.error('Error fetching weekly workout logs:', error);
      } finally {
        setWeeklyLogsLoading(false);
      }
    };
    fetchWeeklyLogs();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !realtimeMetrics || metricsLoading) return;
    const milestones = milestoneService?.checkAllMilestones(user?.id, realtimeMetrics);
    if (milestones?.length > 0) {
      const milestone = milestones?.[0];
      setCurrentMilestone(milestone);
      setShowMilestonePopup(true);
      milestoneService?.createMilestoneNotification(user?.id, milestone);
      checkMilestoneAchievement(milestone);
    }
    checkGoalCompletions(realtimeMetrics);
  }, [realtimeMetrics, user?.id, metricsLoading]);

  const checkMilestoneAchievement = async (milestone) => {
    if (!user?.id || !milestone) return;
    try {
      const achievement = await achievementCardService?.checkMilestoneAchievement(user?.id, milestone);
      if (achievement) {
        setAchievementCard(achievement);
        setTimeout(() => setShowAchievementCard(true), 6000);
      }
    } catch (error) {
      console.error('Error checking milestone achievement:', error);
    }
  };

  const checkGoalCompletions = async (metrics) => {
    if (!user?.id || !metrics) return;
    try {
      const goals = [
        { metric: 'steps', current: metrics?.steps?.current, target: metrics?.steps?.target, unit: 'steps' },
        { metric: 'calories', current: metrics?.calories?.current, target: metrics?.calories?.target, unit: 'kcal' },
        { metric: 'water', current: metrics?.water?.current, target: metrics?.water?.target, unit: 'glasses' },
        { metric: 'sleep', current: metrics?.sleep?.current, target: metrics?.sleep?.target, unit: 'hours' }
      ];
      for (const goal of goals) {
        const achievement = await achievementCardService?.checkGoalCompletion(user?.id, goal);
        if (achievement) {
          setAchievementCard(achievement);
          setShowAchievementCard(true);
          break;
        }
      }
    } catch (error) {
      console.error('Error checking goal completions:', error);
    }
  };

  const [healthMetrics, setHealthMetrics] = useState({
    steps: { current: 0, target: 10000, unit: 'steps', trend: 0 },
    calories: { current: 0, target: 2000, unit: 'kcal', trend: 0 },
    water: { current: 0, target: 8, unit: 'glasses', trend: 0 },
    sleep: { current: 0, target: 8, unit: 'hrs', trend: 0 }
  });

  useEffect(() => {
    const loadingTimer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    if (realtimeMetrics && !metricsLoading) {
      setMetricSyncStatus({ steps: 'synced', calories: 'synced', water: 'synced', sleep: 'synced' });
      setHealthMetrics(prev => ({
        steps: {
          ...prev?.steps,
          current: realtimeMetrics?.steps?.current ?? prev?.steps?.current,
          target: realtimeMetrics?.steps?.target ?? prev?.steps?.target,
          trend: realtimeMetrics?.steps?.trend ?? prev?.steps?.trend
        },
        calories: {
          ...prev?.calories,
          current: realtimeMetrics?.calories?.current ?? prev?.calories?.current,
          target: realtimeMetrics?.calories?.target ?? prev?.calories?.target,
          trend: realtimeMetrics?.calories?.trend ?? prev?.calories?.trend
        },
        water: {
          ...prev?.water,
          current: realtimeMetrics?.water?.current ?? prev?.water?.current,
          target: realtimeMetrics?.water?.target ?? prev?.water?.target,
          trend: realtimeMetrics?.water?.trend ?? prev?.water?.trend
        },
        sleep: {
          ...prev?.sleep,
          current: realtimeMetrics?.sleep?.current ?? prev?.sleep?.current,
          target: realtimeMetrics?.sleep?.target ?? prev?.sleep?.target,
          trend: realtimeMetrics?.sleep?.trend ?? prev?.sleep?.trend,
          sleepQuality: realtimeMetrics?.sleep?.qualityScore ?? prev?.sleep?.sleepQuality
        }
      }));
    }
  }, [realtimeMetrics, metricsLoading]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    const hour = new Date()?.getHours();
    if (hour < 6) { setGreeting('Good night'); setGreetingEmoji('🌙'); }
    else if (hour < 12) { setGreeting('Good morning'); setGreetingEmoji('☀️'); }
    else if (hour < 17) { setGreeting('Good afternoon'); setGreetingEmoji('👋'); }
    else if (hour < 22) { setGreeting('Good evening'); setGreetingEmoji('🌆'); }
    else { setGreeting('Good night'); setGreetingEmoji('🌙'); }
    return () => clearInterval(timer);
  }, []);

  const handleMetricClick = (metricType) => {
    const routeMap = {
      steps: '/activity-tracking',
      calories: '/nutrition-tracking',
      water: '/nutrition-tracking',
      sleep: '/sleep-analysis'
    };
    navigate(routeMap?.[metricType] || '/dashboard');
  };

  const handleProfileClick = () => navigate('/profile-settings');

  const calculatePercentage = (current, target) =>
    Math.min(Math.round((current / target) * 100), 100);

  const metricConfigs = [
    { key: 'steps', title: 'Daily Steps', icon: 'Footprints', color: '#52C41A' },
    { key: 'calories', title: 'Calories', icon: 'Flame', color: '#ED8936' },
    { key: 'water', title: 'Hydration', icon: 'Droplets', color: '#4299E1' },
    { key: 'sleep', title: 'Sleep', icon: 'Moon', color: '#9F7AEA' }
  ];

  const getWellnessScore = () => {
    const scores = metricConfigs?.map(config => {
      const metric = healthMetrics?.[config?.key];
      return calculatePercentage(metric?.current, metric?.target);
    });
    return Math.round(scores?.reduce((sum, score) => sum + score, 0) / scores?.length);
  };

  const wellnessScore = getWellnessScore();

  const getWellnessLabel = (score) => {
    if (score >= 80) return { label: 'Excellent', color: '#52C41A' };
    if (score >= 60) return { label: 'Good', color: '#73D13D' };
    if (score >= 40) return { label: 'Fair', color: '#ED8936' };
    return { label: 'Getting started', color: '#9F7AEA' };
  };

  const wellnessLabel = getWellnessLabel(wellnessScore);

  const firstName = userProfile?.name?.split(' ')?.[0] || 'there';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <motion.div
            className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full mx-auto"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Loading your wellness data...</h2>
            <p className="text-sm text-muted-foreground">Preparing your personalized health insights</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ── HERO SECTION ── */}
        <motion.section
          className={`relative overflow-hidden rounded-3xl text-white p-6 sm:p-8 ${
            isDark
              ? 'bg-gradient-to-br from-[#1A2840] via-[#0F1923] to-[#0D1520]'
              : 'bg-gradient-to-br from-[#2E6FD8] via-[#1E8FD5] to-[#1AAFB0]'
          }`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Decorative blobs */}
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/4 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4 pointer-events-none" />
          <div className="absolute top-1/2 right-1/4 w-24 h-24 rounded-full bg-white/5 pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            {/* Left: greeting + stats */}
            <div className="space-y-4 flex-1 min-w-0">
              {/* Greeting */}
              <div>
                <p className="text-white/70 text-sm font-medium mb-1">
                  {currentTime?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  {greeting}, {firstName}! {greetingEmoji}
                </h1>
              </div>

              {/* Streak pill */}
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <span className="text-lg">🔥</span>
                <span className="text-sm font-semibold">
                  {userProfile?.currentStreak ?? 0} day streak!
                </span>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap gap-3">
                {/* Wellness score */}
                <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-4 py-3">
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
                      <motion.circle
                        cx="18" cy="18" r="15"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 15}`}
                        initial={{ strokeDashoffset: 2 * Math.PI * 15 }}
                        animate={{ strokeDashoffset: 2 * Math.PI * 15 * (1 - wellnessScore / 100) }}
                        transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1], delay: 0.5 }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-bold">{wellnessScore}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-white/70 leading-none mb-0.5">Wellness</p>
                    <p className="text-sm font-bold leading-none">{wellnessLabel?.label}</p>
                  </div>
                </div>

                {/* Points */}
                <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-4 py-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Award" size={20} color="white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 leading-none mb-0.5">Points</p>
                    <p className="text-sm font-bold leading-none">{(userProfile?.totalPoints ?? 0)?.toLocaleString()} pts</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: avatar + member since */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right hidden sm:block">
                <p className="text-white/60 text-xs">Member since</p>
                <p className="text-white font-semibold text-sm">
                  {userProfile?.memberSince
                    ? new Date(userProfile.memberSince)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : 'N/A'}
                </p>
              </div>
              <motion.button
                className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/40 hover:border-white/70 transition-all duration-200 flex-shrink-0 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleProfileClick}
                aria-label={`View profile for ${userProfile?.name}`}
              >
                <img
                  src={userProfile?.avatar}
                  alt={userProfile?.name || 'User avatar'}
                  className="w-full h-full object-cover"
                />
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* ── TODAY'S METRICS ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-foreground">Today's Metrics</h2>
              <p className="text-sm text-muted-foreground">Tap any card to log or view details</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Live
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {metricConfigs?.map((config, index) => {
              const metric = healthMetrics?.[config?.key];
              const percentage = calculatePercentage(metric?.current, metric?.target);
              return (
                <motion.div
                  key={config?.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.2, duration: 0.4 }}
                >
                  <MetricCard
                    title={config?.title}
                    value={metric?.current}
                    target={metric?.target}
                    unit={metric?.unit}
                    icon={config?.icon}
                    color={config?.color}
                    percentage={percentage}
                    trend={metric?.trend}
                    onClick={() => handleMetricClick(config?.key)}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* ── ACTIVITY CHART + QUICK ACTIONS ── */}
        <motion.section
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {/* Weekly Chart */}
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6">
            <WeeklyChart workoutLogs={weeklyWorkoutLogs} loading={weeklyLogsLoading} />
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <QuickActionWidget />
          </div>
        </motion.section>

        {/* ── ACHIEVEMENTS + WELLNESS TIPS ── */}
        <motion.section
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {/* Achievement Badges */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <AchievementBadges syncStatus={metricSyncStatus} />
          </div>

          {/* Wellness Tips */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Sparkles" size={18} color="#52C41A" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">Wellness Tips</h3>
                <p className="text-xs text-muted-foreground">Daily health reminders</p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { icon: 'Activity', color: '#52C41A', bg: '#52C41A15', title: 'Stay Active', desc: 'Aim for 150 min of moderate activity weekly' },
                { icon: 'Droplets', color: '#4299E1', bg: '#4299E115', title: 'Stay Hydrated', desc: 'Drink water consistently throughout the day' },
                { icon: 'Moon', color: '#9F7AEA', bg: '#9F7AEA15', title: 'Quality Sleep', desc: 'Maintain a consistent sleep schedule for recovery' },
              ]?.map((tip, i) => (
                <motion.div
                  key={tip?.title}
                  className="flex items-start gap-3 p-3 rounded-xl border border-border/60 hover:border-border transition-colors"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 + 0.5 }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: tip?.bg }}
                  >
                    <Icon name={tip?.icon} size={15} color={tip?.color} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{tip?.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{tip?.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Motivational quote */}
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/10">
              <p className="text-xs text-center text-muted-foreground italic leading-relaxed">
                "Every step towards better health is a victory worth celebrating." 💚
              </p>
            </div>
          </div>
        </motion.section>

      </main>
      {/* Floating action button */}
      <div className="fixed bottom-6 right-6 z-50">
        <QuickActionButton />
      </div>
      <GoalSettingPanel />
      <MilestonePopup
        milestone={currentMilestone}
        isOpen={showMilestonePopup}
        onClose={() => setShowMilestonePopup(false)}
      />
    </div>
  );
};

export default Dashboard;